import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryCityMarketingPage,
  buildIndustryCityMarketingHead,
  type IndustryCityMarketingConfig,
} from "@/components/IndustryCityMarketingPage";
import { PARKER_PROFILE } from "@/lib/cityProfiles/parker";

const CONFIG: IndustryCityMarketingConfig = {
  slug: "hvac-marketing-parker",
  industry: "HVAC",
  industryLower: "hvac",
  ...PARKER_PROFILE,

  metaTitle: "HVAC Marketing in Parker, CO | SEO, Maps, Ads & Web",
  metaDescription:
    "Owner-led HVAC marketing for Parker — SEO, Google Maps, web design, and ads tuned for Stonegate, Stroh Ranch, and The Pinery. Flat $1,650/mo, no contracts.",

  h1: {
    lead: "HVAC marketing in",
    gold: "Parker",
    trail: "— from Stonegate to The Pinery",
  },
  eyebrow: "Parker HVAC Marketing",
  heroDescription:
    "We help Parker HVAC companies own the Map Pack across 80134 and 80138, win emergency-call SEO, and stay booked year-round serving Stonegate, Stroh Ranch, The Pinery, and Canterberry Crossing.",

  intro:
    "Parker is dense with the exact homeowners HVAC companies want — established neighborhoods like The Pinery and Canterberry Crossing with 15-to-25-year-old systems hitting end-of-life, plus a steady flow of new builds in Idyllwilde and Reata North. The problem isn't demand; it's that a homeowner in Stonegate Googling 'AC repair near me' on a 95° afternoon sees a Map Pack stuffed with Denver-metro chains that barely service south Douglas County. Winning Parker HVAC means owning the local Map Pack, ranking for every neighborhood + service combo, and converting mobile callers in under 3 seconds. That's what we build — owner-led, flat $1,650/month, no contracts.",

  painPoints: [
    "Losing Parker calls to Denver-metro HVAC chains that outrank you in the Map Pack despite barely touching south Douglas County.",
    "Slow swing seasons (April–May, September–October) where lead flow dries up between the AC rush and the furnace rush.",
    "High-ticket full-system and heat-pump replacement leads in The Pinery and Canterberry Crossing going to whichever HVAC company a homeowner finds first on their phone.",
  ],

  services: {
    seo: "Rank for 'AC repair Parker,' 'furnace repair 80134,' and every neighborhood + service combo across Stonegate, Stroh Ranch, The Pinery, and Canterberry Crossing.",
    googleMaps: "Top-3 Map Pack for Parker HVAC — categories tuned for service-area radius across 80134/80138, geo-tagged photos, weekly GBP posts, and a real review-velocity engine.",
    webDesign: "Hand-coded HVAC sites that load sub-2s on a phone in a Twenty Mile Rd parking lot and convert emergency callers with sticky tap-to-call.",
    ppc: "Profitable Google Ads tuned to Parker seasons — emergency-repair bidding ramps in July and December, ROI-tracked per ZIP.",
    geoAi: "Get cited when Parker homeowners ask ChatGPT, Gemini, or Perplexity 'best HVAC company in Parker CO' or 'who installs furnaces near The Pinery.'",
    socialMedia: "Local trust-building content that converts one-time repair customers into annual maintenance contracts across Parker's established neighborhoods.",
  },

  trustPoints: [
    "We work Parker weekly from our Colorado Springs HQ — not a coastal agency, not a national franchise.",
    "300+ Colorado clients served since 2006 across HVAC, plumbing, and electrical.",
    "Flat $1,650/month marketing, websites from $1,850 — published publicly, no discovery-call price reveal.",
    "Talk to Chris directly. No account managers, no sales reps, no offshore handoffs.",
  ],

  testimonial: {
    quote:
      "We'd been spending five figures a month with a Denver agency and getting nothing. Switched to Chris, half the cost, and within 90 days we were in the top-3 of the Parker Map Pack. Phone hasn't stopped since.",
    role: "HVAC company owner",
    location: "Parker, CO",
  },

  faqs: [
    {
      q: "How do I get more HVAC leads in Parker?",
      a: "Local SEO + a tuned Google Business Profile is the highest-ROI play — show up in the Map Pack for 'AC repair Parker' and 'furnace repair near me' across 80134 and 80138, with a website that converts mobile callers in under 3 seconds. Layer Google Ads on during swing seasons (April–May, September–October) to keep techs booked when calls naturally slow.",
    },
    {
      q: "How much does HVAC marketing cost in Parker?",
      a: "Our Growth Marketing package — SEO, Google Maps, GEO AI, and social — is $1,650/month flat. A new HVAC website starts at $1,850 one-time, owned outright. In-house financing is available starting at 4 monthly payments. Prices are published publicly because Parker owners deserve a straight answer.",
    },
    {
      q: "How long until I rank in the Parker Map Pack?",
      a: "Most Parker HVAC clients see meaningful Map Pack movement within 60–120 days. Citywide top-3 across Stonegate, Stroh Ranch, The Pinery, and Canterberry Crossing usually takes 4–6 months of consistent GBP work, citation cleanup, and review generation. It compounds — month 6 is dramatically stronger than month 2.",
    },
    {
      q: "Do you cover Elizabeth, Franktown, and Lone Tree too?",
      a: "Yes. We build service-area copy and Map Pack signals for the corridors your trucks actually reach — Elizabeth, Franktown, Lone Tree, and the south Douglas County / north Elbert County edge. If you serve a ZIP, we make sure that ZIP can find you.",
    },
    {
      q: "Should I run Google Ads or focus on SEO first?",
      a: "Need calls this week? Start Google Ads — we can have a tuned Parker HVAC campaign live in 5 business days. Want a lead engine that doesn't shut off when you pause spend? Start SEO + Google Maps. Most successful Parker HVAC clients run both: ads for immediate flow, SEO for the durable foundation.",
    },
    {
      q: "Are you actually local to Parker?",
      a: "We're headquartered in Colorado Springs (3210 E Woodmen Rd) and work Parker weekly. Verified Google Business Profile, 4.9★ average, Colorado-owned and -operated since 2006. Stop by, meet Chris, or have him drive up — either works.",
    },
    {
      q: "Do you lock me into a contract?",
      a: "No. Every Colorado Web Impressions engagement is month-to-month. If we're not earning our keep, you cancel — no early-termination fees, no clawbacks. We earn the renewal every month.",
    },
  ],

  related: [
    {
      to: "/hvac-marketing-colorado",
      label: "HVAC marketing across Colorado",
      description: "Statewide HVAC marketing hub — every service we offer for Colorado HVAC companies.",
    },
    {
      to: "/parker",
      label: "Parker digital marketing hub",
      description: "Every service we offer for Parker businesses in one place.",
    },
    {
      to: "/hvac-marketing-castle-rock",
      label: "HVAC marketing in Castle Rock",
      description: "Same playbook tuned for Castle Rock HVAC companies.",
    },
    {
      to: "/hvac-marketing-colorado-springs",
      label: "HVAC marketing in Colorado Springs",
      description: "Our flagship HVAC marketing page for El Paso County.",
    },
    {
      to: "/pricing",
      label: "Flat, public pricing",
      description: "$1,650/mo marketing, websites from $1,850 — plus in-house financing for new sites.",
    },
    {
      to: "/meet-chris",
      label: "Meet Chris",
      description: "The owner you'll actually talk to — not a salesperson or account manager.",
    },
  ],

  showPricing: true,
};

export const Route = createFileRoute("/hvac-marketing-parker")({
  head: () => buildIndustryCityMarketingHead(CONFIG),
  component: () => <IndustryCityMarketingPage config={CONFIG} />,
});
